import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const milestones = [
  { year: '2018', title: '平台创立', description: '91平台正式上线，开启互联网服务新篇章。' },
  { year: '2019', title: '用户突破百万', description: '凭借优质内容和流畅体验，注册用户突破100万。' },
  { year: '2020', title: '网页版发布', description: '91网页版入口正式上线，用户无需下载客户端即可访问。' },
  { year: '2021', title: '功能全面升级', description: '网页版功能与客户端全面对齐，获得用户一致好评。' },
  { year: '2022', title: '安全体系完善', description: '引入HTTPS加密和两步验证，全面提升账号安全性。' },
  { year: '2023', title: '移动端适配', description: '完成移动端全面适配，手机访问体验接近原生App。' },
  { year: '2024', title: '性能大优化', description: '页面加载速度提升40%，用户体验达到新高度。' },
  { year: '2025', title: 'AI智能推荐', description: '引入智能推荐算法，为用户提供个性化内容体验。' },
  { year: '2026', title: '全新界面升级', description: '全新UI设计上线，更现代更简洁的使用体验。' },
];

const stats = [
  { value: '500万+', label: '累计用户' },
  { value: '99.9%', label: '服务可用率' },
  { value: '24/7', label: '技术支持' },
  { value: '8年', label: '稳定运营' },
];

export default function About() {
  return (
    <>
      <Navbar />
      <main>
        <div className="pt-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          </div>
        </div>

        {/* Hero */}
        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-900 mb-5">
              关于91网页版入口
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed">
              91网页版入口成立于2018年，致力于为广大用户提供最便捷的91平台网页版访问方式。
              我们始终坚持"无需下载、即开即用"的理念，让每一位用户都能通过浏览器轻松访问91平台的全部功能。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 px-4 md:px-6 bg-background-100/70">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-heading text-primary-600 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-8 text-center">
              品牌故事
            </h2>
            <div className="prose prose-sm max-w-none text-foreground-600 space-y-4">
              <p>
                91网页版入口的诞生源于一个简单的想法：让用户无需下载安装任何软件，就能享受91平台的全部服务。
                在移动互联网高速发展的时代，我们注意到很多用户因为设备存储空间有限、或是在办公场景下不便安装客户端，
                而无法顺畅使用91平台。
              </p>
              <p>
                为此，我们投入大量资源开发和完善91网页版，确保网页版的功能体验与客户端保持一致，
                同时在访问速度和安全性方面持续优化。经过多年的发展，91网页版已经成为数百万用户访问91平台的首选方式。
              </p>
              <p>
                我们的团队始终秉持"用户至上"的原则，不断收集用户反馈，改进产品体验。
                无论是访问教程的撰写、常见问题的解答，还是新功能的开发，我们都力求做到最好，
                让每一位用户都能在91网页版上获得满意的体验。
              </p>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100/70">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'} pl-12 md:pl-0`}>
                      <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 inline-block text-left">
                        <span className="inline-block px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-2">
                          {item.year}
                        </span>
                        <h3 className="text-base font-heading text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-600">{item.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 -translate-x-1/2 mt-6 z-10"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">
              联系我们
            </h2>
            <p className="text-foreground-600 mb-10">
              有任何关于91网页版入口的问题或建议，欢迎通过以下方式联系我们
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-heading text-foreground-900 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-500">contact@91web.example.com</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-phone-line text-xl"></i>
                </div>
                <h3 className="text-sm font-heading text-foreground-900 mb-1">客服热线</h3>
                <p className="text-sm text-foreground-500">400-000-9100</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <h3 className="text-sm font-heading text-foreground-900 mb-1">公司地址</h3>
                <p className="text-sm text-foreground-500">中国上海市浦东新区</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于91网页版入口',
            description: '91网页版入口成立于2018年，致力于为广大用户提供最便捷的91平台网页版访问方式。',
            url: 'https://91web.example.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '91网页版入口',
              url: 'https://91web.example.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://91web.example.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
                item: 'https://91web.example.com/about',
              },
            ],
          }),
        }}
      />
    </>
  );
}